import { useState, useEffect } from 'react';
import { ArrowClockwise, Popcorn, CaretDown, CaretUp, EnvelopeSimple } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Admin-only Dashboard widget listing warm leads captured by Crunchy the
 * salesperson on the public marketing pages (sales_leads collection).
 * Each row expands to show the full chat transcript behind the lead.
 */
export default function SalesLeadsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // conversation_id
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/sales-leads?limit=25')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleTranscript = async (convId) => {
    if (expanded === convId) {
      setExpanded(null);
      setTranscript(null);
      return;
    }
    setExpanded(convId);
    setTranscript(null);
    setTranscriptLoading(true);
    try {
      const r = await api.get(`/admin/sales-leads/transcript/${convId}`);
      setTranscript(r.data.messages || []);
    } catch {
      setTranscript([]);
    } finally {
      setTranscriptLoading(false);
    }
  };

  if (loading && !data) return null;
  if (!data) return null;

  const leads = data.leads || [];
  const interestColor = {
    crm: 'bg-blue-100 text-blue-800',
    migration: 'bg-amber-100 text-amber-800',
    affiliate: 'bg-purple-100 text-purple-800',
  };

  return (
    <div className="border border-[#F39C12]/40 bg-white rounded-sm mb-4" data-testid="sales-leads-widget">
      <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between gap-3 flex-wrap bg-gradient-to-r from-amber-50 to-white">
        <div className="flex items-center gap-2">
          <Popcorn size={18} weight="bold" className="text-[#F39C12]" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#111827]">
            Crunchy&rsquo;s Sales Leads
          </h3>
          <span
            className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-[#F39C12] text-black"
            data-testid="sales-leads-count-pill"
          >
            {data.total} total
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[10px] text-[#6B7280] hover:text-[#F39C12] flex items-center gap-1"
          data-testid="sales-leads-refresh-btn"
        >
          <ArrowClockwise size={11} weight="bold" className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {leads.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-[#6B7280]" data-testid="sales-leads-empty">
          No leads captured yet. Crunchy is working the floor on the homepage,
          /pricing and /affiliates &mdash; when a prospect drops their email,
          it lands here (and in chip@migrationcrunch.com&rsquo;s inbox).
        </div>
      ) : (
        <div className="divide-y divide-black/5">
          {leads.map((lead) => (
            <div key={lead.conversation_id + lead.created_at} data-testid="sales-lead-row">
              <div className="px-4 py-2.5 grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_0.7fr_auto] gap-x-3 gap-y-1 items-center text-xs">
                <div className="min-w-0">
                  <a
                    href={`mailto:${lead.email}`}
                    className="font-semibold text-[#0B5394] hover:underline flex items-center gap-1 truncate"
                    title={lead.email}
                  >
                    <EnvelopeSimple size={12} weight="bold" className="shrink-0" />
                    <span className="truncate">{lead.email}</span>
                  </a>
                  <div className="text-[10px] text-[#6B7280] truncate">
                    {[lead.name, lead.company].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <div className="text-[#4B5563]">
                  <span className="text-[10px] text-[#9CA3AF] block">Current CRM</span>
                  {lead.current_crm || '—'}
                </div>
                <div className="text-[#4B5563]">
                  <span className="text-[10px] text-[#9CA3AF] block">Contacts</span>
                  {lead.contact_count || '—'}
                </div>
                <div>
                  <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${interestColor[lead.interest] || 'bg-slate-100 text-slate-700'}`}>
                    {lead.interest || 'lead'}
                  </span>
                  <div className="text-[9px] text-[#9CA3AF] mt-0.5">
                    {(lead.created_at || '').slice(0, 10)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleTranscript(lead.conversation_id)}
                  className="text-[10px] text-[#6B7280] hover:text-[#0B5394] flex items-center gap-0.5 justify-self-end"
                  data-testid="sales-lead-transcript-btn"
                >
                  {expanded === lead.conversation_id ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
                  Chat
                </button>
                {lead.notes && (
                  <div className="col-span-2 md:col-span-5 text-[11px] text-[#4B5563] italic">
                    &ldquo;{lead.notes}&rdquo;
                  </div>
                )}
              </div>

              {expanded === lead.conversation_id && (
                <div className="px-4 pb-3 bg-slate-50 border-t border-black/5" data-testid="sales-lead-transcript">
                  {transcriptLoading ? (
                    <div className="text-[11px] text-[#6B7280] py-3">Loading transcript…</div>
                  ) : (transcript || []).length === 0 ? (
                    <div className="text-[11px] text-[#6B7280] py-3">Transcript unavailable.</div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 py-3">
                      {transcript.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded px-2.5 py-1.5 text-[11px] whitespace-pre-wrap break-words ${
                            m.role === 'user'
                              ? 'bg-[#0B5394] text-white'
                              : 'bg-white border border-slate-200 text-slate-800'
                          }`}>
                            {m.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
